import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  ArrowLeft, ChevronDown, ChevronUp, Plus, X, Save, Wrench,
  AlertTriangle, Check, ClipboardList,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CustomerPicker, VehiclePicker } from "@/pages/estimates/components/Pickers";
import PhotoUploader, { flushPhotoQueue } from "./components/PhotoUploader";
import DamageMarker from "./components/DamageMarker";
import {
  SERVICE_TYPES, PRIORITIES, COMMON_COMPLAINTS, STATUS_META,
} from "./lib";

const DRAFT_KEY = "fender_jc_draft_new_v1";

/**
 * Fast job-card creation screen (Section 7).
 *
 * The first screen shows ONLY the essentials from spec Section 7:
 *   Customer (name + phone), Vehicle (reg + make + model + variant),
 *   Work Required (complaint + service type + general tasks),
 *   Assignment (service advisor + expected delivery),
 *   Payment (advance yes/no + amount).
 *
 * Everything else is behind the "More Details" collapse.
 */
export default function CreateJobCard() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [advisors, setAdvisors] = useState([]);
  const [tasksMaster, setTasksMaster] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [nextNumber, setNextNumber] = useState("");
  const [dupWarn, setDupWarn] = useState(null);
  const [moreOpen, setMoreOpen] = useState(false);

  // Prefill from appointment or estimate
  const seedApptId = sp.get("appointment_id");
  const seedEstId  = sp.get("estimate_id");
  const seedCustId = sp.get("customer_id");
  const seedVehId  = sp.get("vehicle_id");

  const [form, setForm] = useState(() => {
    try {
      const cached = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
      if (cached) return cached;
    } catch { /* noop */ }
    return {
      customer_id: null, customer_snapshot: null,
      vehicle_id: null, vehicle_snapshot: null,
      complaints: [{ text: "", category: "" }],
      service_type: "",
      secondary_service_types: [],
      repair_type: "direct_customer",
      insurance: null,
      tasks: [],                    // general tasks selected
      service_advisor_id: "",
      priority: "normal",
      promised_at: "",
      advance: { received: false, amount: 0, mode: "cash" },
      // more-details fields
      odometer: "",
      fuel_level: "half",
      belongings: "",
      customer_commitment_note: "",
      internal_notes: "",
      damage_markers: [],
      photo_queue: [],   // items waiting to be uploaded after JC create
    };
  });

  useEffect(() => {
    api.get("/job-cards/next-number").then((r) => setNextNumber(r.data?.next_number || "")).catch(() => {});
    api.get("/job-cards/service-advisors").then((r) => setAdvisors(r.data || [])).catch(() => setAdvisors([]));
    api.get("/master/service-tasks-v2").then((r) => {
      const list = r.data || [];
      // Master returns categorised object OR array — normalise to array
      if (Array.isArray(list)) setTasksMaster(list);
      else {
        const flat = [];
        for (const [cat, tasks] of Object.entries(list)) {
          (tasks || []).forEach((t) => flat.push({
            id: t.id || t, task_name: typeof t === "string" ? t : (t.task_name || t.name || t.label),
            category: cat,
          }));
        }
        setTasksMaster(flat);
      }
    }).catch(() => setTasksMaster([]));
  }, []);

  // Prefill from appointment
  useEffect(() => {
    if (!seedApptId) return;
    api.get(`/appointments/${seedApptId}`).then((r) => {
      const a = r.data;
      setForm((f) => ({
        ...f,
        customer_id: a.customer_id, customer_snapshot: a.customer_snapshot,
        vehicle_id: a.vehicle_id, vehicle_snapshot: a.vehicle_snapshot,
        complaints: a.complaints ? [{ text: a.complaints, category: "" }] : f.complaints,
        service_advisor_id: a.assignee_id || f.service_advisor_id,
      }));
      toast.info(`Prefilled from appointment ${a.number || a.id?.slice(0,8)}`);
    }).catch(() => {});
     
  }, [seedApptId]);

  // Prefill from estimate
  useEffect(() => {
    if (!seedEstId) return;
    api.get(`/estimates/${seedEstId}`).then((r) => {
      const e = r.data;
      setForm((f) => ({
        ...f,
        customer_id: e.customer_id, customer_snapshot: e.customer_snapshot,
        vehicle_id: e.vehicle_id, vehicle_snapshot: e.vehicle_snapshot,
        complaints: (e.complaints || []).length
          ? e.complaints.map((c) => ({ text: c.text, category: c.category || "" }))
          : f.complaints,
        tasks: (e.labor || []).map((l) => ({
          task_name: l.task_name, category: "", priority: "normal", hours_est: l.qty || 1,
          status: "not_started", customer_approved: true,
        })),
      }));
      toast.info(`Prefilled from estimate ${e.number}`);
    }).catch(() => {});
     
  }, [seedEstId]);

  // Prefill from customer / vehicle
  useEffect(() => {
    if (!seedCustId && !seedVehId) return;
    (async () => {
      const upd = {};
      if (seedCustId) {
        const r = await api.get(`/customers/${seedCustId}`).catch(() => null);
        if (r?.data) {
          upd.customer_id = r.data.id;
          upd.customer_snapshot = {
            id: r.data.id, name: r.data.name, phone: r.data.phone,
            email: r.data.email, address: r.data.address,
          };
        }
      }
      if (seedVehId) {
        const r = await api.get("/vehicles", { params: { customer_id: seedCustId } }).catch(() => null);
        const veh = (r?.data || []).find((v) => v.id === seedVehId);
        if (veh) {
          upd.vehicle_id = veh.id;
          upd.vehicle_snapshot = { id: veh.id, reg_number: veh.reg_number, make: veh.make, model: veh.model, variant: veh.variant, fuel_type: veh.fuel_type, year: veh.year, odometer: veh.odometer };
        }
      }
      if (Object.keys(upd).length) setForm((f) => ({ ...f, ...upd }));
    })();
     
  }, [seedCustId, seedVehId]);

  // Draft autosave — throttled.  We strip File objects from the photo queue
  // since they're not JSON-serialisable; the preview data URL is enough for
  // draft restoration and we prompt the user to re-attach after reload.
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        const persist = { ...form, photo_queue: [] };
        localStorage.setItem(DRAFT_KEY, JSON.stringify(persist));
      } catch { /* quota */ }
    }, 800);
    return () => clearTimeout(t);
  }, [form]);

  // Duplicate-active-JC check on vehicle change
  useEffect(() => {
    if (!form.vehicle_id) { setDupWarn(null); return; }
    api.get("/job-cards/duplicate-check", { params: { vehicle_id: form.vehicle_id } })
      .then((r) => {
        if (r.data.has_active) setDupWarn(r.data.matches);
        else setDupWarn(null);
      })
      .catch(() => setDupWarn(null));
  }, [form.vehicle_id]);

  const canSave =
    form.customer_snapshot?.name &&
    form.customer_snapshot?.phone &&
    form.vehicle_snapshot?.reg_number &&
    form.complaints.some((c) => c.text.trim());

  const addComplaint = () => setForm((f) => ({ ...f, complaints: [...f.complaints, { text: "", category: "" }] }));
  const removeComplaint = (i) => setForm((f) => ({
    ...f, complaints: f.complaints.filter((_, idx) => idx !== i),
  }));
  const updateComplaint = (i, k, v) => setForm((f) => ({
    ...f, complaints: f.complaints.map((c, idx) => idx === i ? { ...c, [k]: v } : c),
  }));

  const addTask = (task) => {
    if (!task) return;
    setForm((f) => ({
      ...f,
      tasks: [...f.tasks, {
        task_id: task.id, task_name: task.task_name || task.name,
        category: task.category || "", priority: "normal", hours_est: 1,
        status: "not_started", customer_approved: true,
      }],
    }));
  };
  const removeTask = (i) => setForm((f) => ({ ...f, tasks: f.tasks.filter((_, idx) => idx !== i) }));

  const submit = async (asDraft = false) => {
    if (!asDraft && !canSave) {
      setError("Customer, vehicle, and at least one complaint are required");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        customer_id: form.customer_id || form.customer_snapshot?.id,
        vehicle_id: form.vehicle_id || form.vehicle_snapshot?.id,
        customer_snapshot: form.customer_snapshot,
        vehicle_snapshot: form.vehicle_snapshot,
        appointment_id: seedApptId || null,
        estimate_id: seedEstId || null,
        complaints: form.complaints.filter((c) => c.text.trim()),
        service_type: form.service_type,
        secondary_service_types: form.secondary_service_types,
        repair_type: form.repair_type,
        insurance: form.repair_type === "insurance" ? form.insurance : null,
        tasks: form.tasks,
        service_advisor_id: form.service_advisor_id || null,
        priority: form.priority,
        promised_at: form.promised_at || null,
        advance: form.advance.received ? form.advance : { received: false },
        odometer: form.odometer ? Number(form.odometer) : null,
        fuel_level: form.fuel_level,
        belongings: form.belongings,
        customer_commitment_note: form.customer_commitment_note,
        internal_notes: form.internal_notes,
        damage_markers: form.damage_markers || [],
        status: asDraft ? "draft" : "vehicle_received",
      };
      const r = await api.post("/job-cards", payload);
      const jc = r.data;

      // Flush queued photos → uploads endpoint with ref_id = jc.id
      if (form.photo_queue && form.photo_queue.length) {
        toast.info(`Uploading ${form.photo_queue.length} photo(s)…`);
        const ok = await flushPhotoQueue(form.photo_queue, jc.id, "job_card");
        if (ok < form.photo_queue.length) {
          toast.warning(`Only ${ok}/${form.photo_queue.length} photos uploaded. Add the rest on the Photos tab.`);
        }
      }

      localStorage.removeItem(DRAFT_KEY);
      toast.success(`Job card ${jc.number} created`);
      if (jc._warning) toast.warning(jc._warning);
      nav(`/job-cards/${jc.id}`);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not create job card");
    } finally {
      setSaving(false);
    }
  };

  const clearDraft = () => {
    if (!window.confirm("Discard this draft?")) return;
    localStorage.removeItem(DRAFT_KEY);
    setForm({
      customer_id: null, customer_snapshot: null,
      vehicle_id: null, vehicle_snapshot: null,
      complaints: [{ text: "", category: "" }],
      service_type: "", secondary_service_types: [],
      repair_type: "direct_customer", insurance: null,
      tasks: [], service_advisor_id: "",
      priority: "normal", promised_at: "",
      advance: { received: false, amount: 0, mode: "cash" },
      odometer: "", fuel_level: "half", belongings: "",
      customer_commitment_note: "", internal_notes: "",
      damage_markers: [], photo_queue: [],
    });
  };

  return (
    <div className="pb-32 md:pb-8" data-testid="create-jobcard-page">
      {/* Header */}
      <header className="flex items-center gap-3 mb-4">
        <Link
          to="/job-cards"
          className="w-9 h-9 grid place-items-center rounded-md hover:bg-[#F1FAEE] text-[#457b9d]"
          data-testid="jc-back"
        >
          <ArrowLeft size={17} />
        </Link>
        <div>
          <h1 className="text-[22px] md:text-[26px] font-bold text-[#1d3557] leading-tight">
            New Job Card
          </h1>
          <p className="text-[12px] text-[#667085] mt-0.5">
            Receive a vehicle and start work in under two minutes.
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={clearDraft}
            className="h-9 px-3 rounded-md border border-[#E4E7EC] text-[11.5px] font-semibold text-[#667085] hover:bg-[#F1FAEE]"
            data-testid="jc-discard-draft"
          >
            Discard
          </button>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left column — flow */}
        <div className="lg:col-span-2 space-y-5">
          {/* Duplicate warning */}
          {dupWarn && dupWarn.length > 0 && (
            <div className="rounded-lg border border-[#F79009]/40 bg-[#FEF0C7] px-3.5 py-2.5 text-[12.5px] text-[#7c4a02] flex items-start gap-2" data-testid="jc-dup-warn">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold">This vehicle already has an active job card</div>
                <div className="mt-1 space-x-2">
                  {dupWarn.slice(0, 3).map((m) => (
                    <Link key={m.id} to={`/job-cards/${m.id}`} className="font-mono font-semibold underline">
                      {m.number || m.job_number}
                    </Link>
                  ))}
                  <span className="opacity-70">— proceed only if this is a separate visit.</span>
                </div>
              </div>
            </div>
          )}

          {/* Customer */}
          <Section title="Customer" testId="jc-section-customer" required>
            <CustomerPicker
              value={form.customer_snapshot}
              onChange={(c) => setForm({
                ...form,
                customer_id: c?.id || null,
                customer_snapshot: c,
                vehicle_id: null, vehicle_snapshot: null,
              })}
            />
          </Section>

          {/* Vehicle */}
          <Section title="Vehicle" testId="jc-section-vehicle" required>
            <VehiclePicker
              value={form.vehicle_snapshot}
              customerId={form.customer_id || form.customer_snapshot?.id}
              onChange={(v) => setForm({ ...form, vehicle_id: v?.id || null, vehicle_snapshot: v })}
            />
          </Section>

          {/* Work required — Complaints + Service Type + Tasks */}
          <Section title="Work Required" testId="jc-section-work" required>
            <div className="space-y-3">
              <div>
                <Label>Customer complaints</Label>
                {form.complaints.map((c, i) => (
                  <div key={i} className="mt-1 flex items-start gap-2">
                    <textarea
                      rows={i === 0 ? 2 : 1}
                      value={c.text}
                      onChange={(e) => updateComplaint(i, "text", e.target.value)}
                      list={`jc-common-complaints-${i}`}
                      data-testid={`jc-complaint-${i}`}
                      placeholder="e.g. AC not cooling, brake noise, service due…"
                      className="flex-1 px-2.5 py-1.5 rounded-md border border-[#E4E7EC] text-[13px] bg-white focus:border-[#457b9d] outline-none"
                    />
                    {form.complaints.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeComplaint(i)}
                        className="text-[#a8dadc] hover:text-[#e63946] h-8 w-8 grid place-items-center"
                        data-testid={`jc-complaint-remove-${i}`}
                      >
                        <X size={13} />
                      </button>
                    )}
                  </div>
                ))}
                <div className="mt-2 flex gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={addComplaint}
                    className="h-8 px-3 rounded-md bg-[#F1FAEE] text-[#1d3557] text-[11.5px] font-semibold hover:bg-[#a8dadc]/40 inline-flex items-center gap-1"
                    data-testid="jc-complaint-add"
                  >
                    <Plus size={11} /> Add complaint
                  </button>
                  <details className="ml-1">
                    <summary className="h-8 px-3 rounded-md border border-[#E4E7EC] text-[11.5px] font-semibold text-[#667085] hover:bg-white cursor-pointer inline-flex items-center gap-1">
                      Common complaints
                    </summary>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {COMMON_COMPLAINTS.map((t) => (
                        <button
                          key={t}
                          type="button"
                          onClick={() => setForm((f) => ({
                            ...f, complaints: [...f.complaints.filter((c) => c.text.trim()), { text: t, category: "" }],
                          }))}
                          className="h-7 px-2.5 rounded-full bg-white border border-[#E4E7EC] text-[11px] text-[#457b9d] hover:bg-[#F1FAEE]"
                        >
                          + {t}
                        </button>
                      ))}
                    </div>
                  </details>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="block">
                  <Label>Service type</Label>
                  <select
                    value={form.service_type}
                    onChange={(e) => setForm({ ...form, service_type: e.target.value })}
                    data-testid="jc-service-type"
                    className="mt-1 w-full h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] bg-white focus:border-[#457b9d] outline-none"
                  >
                    <option value="">Select…</option>
                    {SERVICE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
                <label className="block">
                  <Label>Priority</Label>
                  <select
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: e.target.value })}
                    data-testid="jc-priority"
                    className="mt-1 w-full h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] bg-white focus:border-[#457b9d] outline-none"
                  >
                    {PRIORITIES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                  </select>
                </label>
              </div>

              <div>
                <Label>General tasks</Label>
                <TaskPicker master={tasksMaster} onPick={addTask} testId="jc-task-picker" />
                {form.tasks.length > 0 && (
                  <ul className="mt-2 space-y-1.5" data-testid="jc-task-list">
                    {form.tasks.map((t, i) => (
                      <li key={i} className="flex items-center gap-2 bg-[#F7F9FC] border border-[#E4E7EC] rounded-md px-2.5 py-2 text-[12.5px]">
                        <ClipboardList size={12} className="text-[#457b9d]" />
                        <span className="flex-1 truncate">
                          <span className="font-semibold text-[#1d3557]">{t.task_name}</span>
                          {t.category && <span className="text-[#667085] ml-2 text-[11px]">· {t.category}</span>}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeTask(i)}
                          className="text-[#a8dadc] hover:text-[#e63946]"
                        >
                          <X size={12} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </Section>

          {/* Assignment */}
          <Section title="Assignment" testId="jc-section-assignment">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="block">
                <Label>Service advisor</Label>
                <select
                  value={form.service_advisor_id || ""}
                  onChange={(e) => setForm({ ...form, service_advisor_id: e.target.value })}
                  data-testid="jc-advisor"
                  className="mt-1 w-full h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] bg-white focus:border-[#457b9d] outline-none"
                >
                  <option value="">Unassigned</option>
                  {advisors.map((a) => (
                    <option key={a.id} value={a.id}>
                      {(a.name || a.email)}{a.role ? ` · ${a.role}` : ""}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <Label>Expected delivery</Label>
                <input
                  type="datetime-local"
                  value={form.promised_at}
                  onChange={(e) => setForm({ ...form, promised_at: e.target.value })}
                  data-testid="jc-promised-at"
                  className="mt-1 w-full h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] bg-white focus:border-[#457b9d] outline-none"
                />
              </label>
            </div>
          </Section>

          {/* Payment */}
          <Section title="Advance Payment" testId="jc-section-payment">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.advance.received}
                onChange={(e) => setForm({
                  ...form,
                  advance: { ...form.advance, received: e.target.checked },
                })}
                data-testid="jc-advance-received"
                className="w-4 h-4 accent-[#e63946]"
              />
              <span className="text-[13px] font-semibold text-[#1d3557]">Advance received</span>
            </label>
            {form.advance.received && (
              <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3">
                <label className="block">
                  <Label>Amount (₹)</Label>
                  <input
                    type="number"
                    value={form.advance.amount}
                    onChange={(e) => setForm({ ...form, advance: { ...form.advance, amount: Number(e.target.value) || 0 } })}
                    data-testid="jc-advance-amount"
                    className="mt-1 w-full h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] bg-white focus:border-[#457b9d] outline-none font-mono"
                  />
                </label>
                <label className="block">
                  <Label>Mode</Label>
                  <select
                    value={form.advance.mode}
                    onChange={(e) => setForm({ ...form, advance: { ...form.advance, mode: e.target.value } })}
                    data-testid="jc-advance-mode"
                    className="mt-1 w-full h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] bg-white focus:border-[#457b9d] outline-none"
                  >
                    {["cash", "upi", "card", "bank_transfer", "other"].map((m) => (
                      <option key={m} value={m}>{m.replace("_", " ")}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <Label>Reference #</Label>
                  <input
                    type="text"
                    value={form.advance.reference_number || ""}
                    onChange={(e) => setForm({ ...form, advance: { ...form.advance, reference_number: e.target.value } })}
                    data-testid="jc-advance-ref"
                    className="mt-1 w-full h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] bg-white focus:border-[#457b9d] outline-none"
                  />
                </label>
              </div>
            )}
          </Section>

          {/* Photos — visible on first paint (spec §12 recommends this at check-in) */}
          <Section title="Vehicle Photos" testId="jc-section-photos">
            <p className="text-[11.5px] text-[#667085] mb-2.5">
              Tap a chip below to shoot or attach photos of the vehicle. They&apos;ll be saved to this job card when you create it.
            </p>
            <PhotoUploader
              queue={form.photo_queue}
              onQueueChange={(q) => setForm({ ...form, photo_queue: q })}
              refId={null}
              context="job_card"
              testId="jc-photos-uploader"
            />
          </Section>

          {/* More Details — collapsed */}
          <section className="bg-white rounded-xl border border-[#E4E7EC]" data-testid="jc-more-details">
            <button
              type="button"
              onClick={() => setMoreOpen((v) => !v)}
              className="w-full flex items-center justify-between px-4 py-3.5 text-left hover:bg-[#F7F9FC]"
              data-testid="jc-more-toggle"
            >
              <div>
                <div className="text-[13.5px] font-bold text-[#1d3557]">More Details</div>
                <div className="text-[11.5px] text-[#667085]">Repair type, insurance, odometer, fuel, notes.</div>
              </div>
              {moreOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {moreOpen && (
              <div className="border-t border-[#F1FAEE] p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label className="block">
                    <Label>Repair type</Label>
                    <select
                      value={form.repair_type}
                      onChange={(e) => setForm({ ...form, repair_type: e.target.value })}
                      data-testid="jc-repair-type"
                      className="mt-1 w-full h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] bg-white focus:border-[#457b9d] outline-none"
                    >
                      <option value="direct_customer">Direct Customer</option>
                      <option value="insurance">Insurance</option>
                      <option value="warranty">Warranty</option>
                      <option value="internal">Internal</option>
                      <option value="other">Other</option>
                    </select>
                  </label>
                  <label className="block">
                    <Label>Odometer (km)</Label>
                    <input
                      type="number"
                      value={form.odometer}
                      onChange={(e) => setForm({ ...form, odometer: e.target.value })}
                      data-testid="jc-odometer"
                      className="mt-1 w-full h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] bg-white focus:border-[#457b9d] outline-none font-mono"
                    />
                  </label>
                  <label className="block">
                    <Label>Fuel level</Label>
                    <select
                      value={form.fuel_level}
                      onChange={(e) => setForm({ ...form, fuel_level: e.target.value })}
                      data-testid="jc-fuel-level"
                      className="mt-1 w-full h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] bg-white focus:border-[#457b9d] outline-none"
                    >
                      <option value="empty">Empty</option>
                      <option value="quarter">1/4</option>
                      <option value="half">1/2</option>
                      <option value="three_quarter">3/4</option>
                      <option value="full">Full</option>
                    </select>
                  </label>
                  <label className="block">
                    <Label>Belongings on board</Label>
                    <input
                      type="text"
                      value={form.belongings}
                      onChange={(e) => setForm({ ...form, belongings: e.target.value })}
                      data-testid="jc-belongings"
                      placeholder="e.g. music system, spare key"
                      className="mt-1 w-full h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] bg-white focus:border-[#457b9d] outline-none"
                    />
                  </label>
                </div>

                {form.repair_type === "insurance" && (
                  <div className="rounded-lg bg-[#F1FAEE] border border-[#a8dadc]/40 p-3" data-testid="jc-insurance-block">
                    <div className="text-[11px] uppercase tracking-wider font-semibold text-[#457b9d] mb-2">Insurance details</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <input type="text" placeholder="Insurance company"
                        value={form.insurance?.company || ""}
                        onChange={(e) => setForm({ ...form, insurance: { ...(form.insurance || {}), company: e.target.value } })}
                        data-testid="jc-ins-company"
                        className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] bg-white focus:border-[#457b9d] outline-none" />
                      <input type="text" placeholder="Policy number"
                        value={form.insurance?.policy_number || ""}
                        onChange={(e) => setForm({ ...form, insurance: { ...(form.insurance || {}), policy_number: e.target.value } })}
                        data-testid="jc-ins-policy"
                        className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] bg-white focus:border-[#457b9d] outline-none" />
                      <input type="text" placeholder="Claim number"
                        value={form.insurance?.claim_number || ""}
                        onChange={(e) => setForm({ ...form, insurance: { ...(form.insurance || {}), claim_number: e.target.value } })}
                        data-testid="jc-ins-claim"
                        className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] bg-white focus:border-[#457b9d] outline-none" />
                      <input type="text" placeholder="Surveyor name"
                        value={form.insurance?.surveyor_name || ""}
                        onChange={(e) => setForm({ ...form, insurance: { ...(form.insurance || {}), surveyor_name: e.target.value } })}
                        data-testid="jc-ins-surveyor"
                        className="h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] bg-white focus:border-[#457b9d] outline-none" />
                    </div>
                    <label className="mt-2.5 inline-flex items-center gap-2 text-[12.5px] text-[#1d3557]">
                      <input type="checkbox"
                        checked={!!form.insurance?.cashless}
                        onChange={(e) => setForm({ ...form, insurance: { ...(form.insurance || {}), cashless: e.target.checked } })}
                        data-testid="jc-ins-cashless"
                        className="w-3.5 h-3.5 accent-[#e63946]" />
                      Cashless
                    </label>
                  </div>
                )}

                <label className="block">
                  <Label>Customer commitment note</Label>
                  <textarea
                    rows={2}
                    value={form.customer_commitment_note}
                    onChange={(e) => setForm({ ...form, customer_commitment_note: e.target.value })}
                    data-testid="jc-commitment-note"
                    placeholder="Anything you promised the customer at check-in…"
                    className="mt-1 w-full px-2 py-1.5 rounded-md border border-[#E4E7EC] text-[13px] bg-white focus:border-[#457b9d] outline-none"
                  />
                </label>

                <label className="block">
                  <Label>Internal notes (not shown to customer)</Label>
                  <textarea
                    rows={2}
                    value={form.internal_notes}
                    onChange={(e) => setForm({ ...form, internal_notes: e.target.value })}
                    data-testid="jc-internal-notes"
                    placeholder="Advisor observations, bay assignment, follow-ups…"
                    className="mt-1 w-full px-2 py-1.5 rounded-md border border-[#E4E7EC] text-[13px] bg-white focus:border-[#457b9d] outline-none"
                  />
                </label>

                {/* Damage marker */}
                <div className="pt-3 border-t border-[#F1FAEE]">
                  <div className="text-[11px] uppercase tracking-wider font-semibold text-[#667085] mb-2">
                    Vehicle condition (damage markers)
                  </div>
                  <DamageMarker
                    markers={form.damage_markers}
                    onChange={(next) => setForm({ ...form, damage_markers: next })}
                    refId={null}
                  />
                </div>
              </div>
            )}
          </section>

          {error && (
            <div className="rounded-lg border border-[#e63946]/30 bg-[#e63946]/10 px-3 py-2 text-[12px] text-[#e63946]" data-testid="jc-form-error">
              {error}
            </div>
          )}
        </div>

        {/* Right sticky summary */}
        <aside className="hidden lg:block">
          <div className="sticky top-6 space-y-3">
            <div className="rounded-xl border border-[#E4E7EC] bg-white p-4" data-testid="jc-summary-panel">
              <div className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Job card</div>
              <div className="text-[16px] font-bold text-[#1d3557] font-mono mt-0.5">
                {nextNumber || "JC-…"}
              </div>
              <dl className="mt-3 space-y-2 text-[12.5px]">
                <SummaryRow k="Customer" v={form.customer_snapshot?.name} />
                <SummaryRow k="Vehicle" v={form.vehicle_snapshot?.reg_number} mono />
                <SummaryRow k="Service" v={form.service_type} />
                <SummaryRow k="Advisor" v={advisors.find((a) => a.id === form.service_advisor_id)?.name} />
                <SummaryRow k="Priority" v={PRIORITIES.find((p) => p.key === form.priority)?.label} />
                <SummaryRow k="Advance" v={form.advance.received ? `₹${form.advance.amount}` : "—"} />
              </dl>
              <div className="pt-3 mt-3 border-t border-[#F1FAEE] space-y-2">
                <button
                  type="button"
                  onClick={() => submit(false)}
                  disabled={!canSave || saving}
                  data-testid="jc-save-btn"
                  className="w-full h-11 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[13px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Save size={13} /> {saving ? "Creating…" : "Create Job Card"}
                </button>
                <button
                  type="button"
                  onClick={() => submit(true)}
                  disabled={saving || !form.customer_snapshot?.name}
                  data-testid="jc-save-draft-btn"
                  className="w-full h-9 rounded-md border border-[#E4E7EC] text-[12px] font-semibold text-[#667085] hover:bg-[#F1FAEE] inline-flex items-center justify-center gap-1"
                >
                  Save as draft
                </button>
              </div>
            </div>
          </div>
        </aside>
      </div>

      {/* Mobile sticky footer */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-[#E4E7EC] p-3 flex gap-2 z-40" data-testid="jc-mobile-footer">
        <button
          type="button"
          onClick={() => submit(true)}
          disabled={saving || !form.customer_snapshot?.name}
          className="flex-1 h-11 rounded-md border border-[#E4E7EC] text-[12.5px] font-semibold text-[#667085] disabled:opacity-50"
          data-testid="jc-save-draft-btn-m"
        >
          Save draft
        </button>
        <button
          type="button"
          onClick={() => submit(false)}
          disabled={!canSave || saving}
          className="flex-1 h-11 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[13px] font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-1"
          data-testid="jc-save-btn-m"
        >
          <Save size={13} /> {saving ? "Creating…" : "Create Job Card"}
        </button>
      </div>
    </div>
  );
}

function Section({ title, children, testId, required }) {
  return (
    <section className="bg-white rounded-xl border border-[#E4E7EC] p-4" data-testid={testId}>
      <div className="text-[13.5px] font-bold text-[#1d3557] mb-3 flex items-center gap-1.5">
        {title}
        {required && <span className="text-[#e63946] text-[11px]">*</span>}
      </div>
      {children}
    </section>
  );
}

function Label({ children }) {
  return <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">{children}</span>;
}

function SummaryRow({ k, v, mono }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-[#667085] text-[11px] uppercase tracking-wider font-semibold">{k}</span>
      <span className={`text-[#1d3557] text-right truncate max-w-[60%] ${mono ? "font-mono" : "font-semibold"}`}>
        {v || "—"}
      </span>
    </div>
  );
}

function TaskPicker({ master, onPick, testId }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const results = useMemo(() => {
    if (!q) return master.slice(0, 12);
    const lc = q.toLowerCase();
    return master.filter((t) => (t.task_name || "").toLowerCase().includes(lc)
                              || (t.category || "").toLowerCase().includes(lc)).slice(0, 20);
  }, [q, master]);

  return (
    <div className="relative mt-1" data-testid={testId}>
      <input
        type="text"
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 160)}
        placeholder="Search a task — engine oil, brake pad, alignment…"
        className="w-full h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] bg-white focus:border-[#457b9d] outline-none"
        data-testid="jc-task-picker-input"
      />
      {open && results.length > 0 && (
        <div className="absolute z-20 top-11 left-0 right-0 bg-white border border-[#E4E7EC] rounded-lg shadow-lg max-h-72 overflow-auto">
          {results.map((t, i) => (
            <button
              key={t.id || i}
              type="button"
              onMouseDown={() => { onPick(t); setQ(""); }}
              data-testid={`jc-task-opt-${i}`}
              className="w-full text-left px-3 py-2 hover:bg-[#F1FAEE] text-[12.5px] text-[#1d3557] flex items-center gap-2"
            >
              <Wrench size={11} className="text-[#457b9d]" />
              <span className="flex-1">{t.task_name || t.name || "—"}</span>
              {t.category && <span className="text-[11px] text-[#667085]">{t.category}</span>}
            </button>
          ))}
          {q && (
            <button
              type="button"
              onMouseDown={() => { onPick({ task_name: q, category: "Custom" }); setQ(""); }}
              className="w-full text-left px-3 py-2 border-t border-[#F1FAEE] text-[12px] font-semibold text-[#e63946] hover:bg-[#F7F9FC]"
              data-testid="jc-task-add-custom"
            >
              + Add custom task &quot;{q}&quot;
            </button>
          )}
        </div>
      )}
    </div>
  );
}
